import { useEffect, useRef, useState } from 'react'
import { com } from '@eidu/form'
import type { RuntimeModule } from 'runtimes-shared'
import { useLocalRuntimes } from '../pages/entity/kinds/asset/AssetPreviewView'
import loadLocalRuntimeMetadata from '../../runtime-packages/runtime-container/src/local-runtime-metadata'
import { getResourceKeyFromEntity } from './entityResourceKeyLink'
import getResourceUrl from './api/resource/getResourceUrl'
import useEntityById from './useEntityById'
import type { KnownKindKey } from '../domain/kindRegistry'
import EntityId = com.eidu.sharedlib.entity.EntityId

type RuntimeMetadata = {
  jsonSchema: Record<string, unknown> | undefined
  documentation: string | undefined
  loading: boolean
}

type MetadataExports = {
  getPayloadSchema?: () => Record<string, unknown>
  getDocumentation?: () => string
}

const metadataCache = new Map<string, { jsonSchema?: Record<string, unknown>; documentation?: string }>()

const loadRemoteRuntimeModule = async (resourceUrl: string): Promise<RuntimeModule | undefined> => {
  const response = await fetch(resourceUrl)
  if (!response.ok) return undefined
  const src = await response.text()
  const blobUrl = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }))
  try {
    const mod = await import(/* @vite-ignore */ blobUrl)
    return (mod.default ?? mod) as RuntimeModule
  } finally {
    URL.revokeObjectURL(blobUrl)
  }
}

const useRuntimeMetadata = (
  runtimeEntityId: EntityId | undefined,
  kindKey: KnownKindKey | undefined
): RuntimeMetadata => {
  const [metadata, setMetadata] = useState<RuntimeMetadata>({
    jsonSchema: undefined,
    documentation: undefined,
    loading: false,
  })
  const { entity: runtimeEntity } = useEntityById(runtimeEntityId)
  const loadingRef = useRef<string | undefined>()

  const cacheKey = useLocalRuntimes && kindKey ? `local:${kindKey}` : runtimeEntityId?.toString()

  useEffect(() => {
    if (!cacheKey) {
      setMetadata({ jsonSchema: undefined, documentation: undefined, loading: false })
      return
    }

    const cached = metadataCache.get(cacheKey)
    if (cached) {
      setMetadata({ jsonSchema: cached.jsonSchema, documentation: cached.documentation, loading: false })
      return
    }

    let cancelled = false
    loadingRef.current = cacheKey
    setMetadata((prev) => ({ ...prev, loading: true }))

    const load = async () => {
      let exports: MetadataExports | undefined

      if (useLocalRuntimes && kindKey) {
        exports = await loadLocalRuntimeMetadata(kindKey)
      } else if (runtimeEntity) {
        const resourceKey = getResourceKeyFromEntity(runtimeEntity)
        if (resourceKey) {
          exports = await loadRemoteRuntimeModule(getResourceUrl(resourceKey))
        }
      } else {
        return
      }

      if (cancelled || loadingRef.current !== cacheKey) return

      const result = {
        jsonSchema: exports?.getPayloadSchema?.(),
        documentation: exports?.getDocumentation?.(),
      }
      metadataCache.set(cacheKey, result)
      setMetadata({ ...result, loading: false })
    }

    load().catch((e) => {
      console.error('[useRuntimeMetadata] Failed to load runtime module', e)
      if (!cancelled) setMetadata({ jsonSchema: undefined, documentation: undefined, loading: false })
    })

    return () => {
      cancelled = true
    }
  }, [cacheKey, runtimeEntity, kindKey])

  return metadata
}

export default useRuntimeMetadata
